import {
  AoVerzekering,
  Dekking,
  UitkeringAoSoort,
} from "../../../../.generated/rapportage/rapportagetypes";

import { Column } from "../../components/BaseTable";
import { bedragFormat } from "../../../../shared/utils/currency";

const getUitkeringBijGedeeltelijkeArbeidsongechiktheid = (
  uitkeringBijAo: UitkeringAoSoort | null
): string | null => {
  switch (uitkeringBijAo) {
    case UitkeringAoSoort.Volledig:
      return "Volledige uitkering";
    case UitkeringAoSoort.ProRata:
      return "Uitkering naar rato";
    default:
      return null;
  }
};

export const getAovColumns = ({
  aoVerzekering,
  isHuidig,
}: {
  aoVerzekering: AoVerzekering;
  isHuidig: boolean;
}): Column[] => {
  const result: Column[] = [
    {
      header: `AOV/${aoVerzekering.product}`,
      accessor: () => " ",
    },
    {
      header: "Lopende of nieuwe verzekering",
      accessor: (data: AoVerzekering) => {
        if (isHuidig) {
          return "Bestaande polis";
        }
        if (data.isLopend) {
          return "Lopende polis";
        }
        return "Nieuwe polis";
      },
    },
    {
      header: "Verzekeraar",
      accessor: "naamVerzekeraar",
    },
    {
      header: "Polisnummer",
      accessor: (data: AoVerzekering) =>
        !!data.polisNummer ? data.polisNummer : null,
    },
    {
      header: "Ingangsdatum",
      accessor: "ingangsdatum",
      dataType: "date",
    },
    {
      header: "Einddatum",
      accessor: "eindDatum",
      dataType: "date",
    },
    {
      header: "Ingangsdatum premiebetaling",
      accessor: "ingangsdatumPremieBetaling",
      dataType: "date",
    },
    {
      header: "Einddatum premiebetaling",
      accessor: "eindDatumPremieBetaling",
      dataType: "date",
    },
    {
      header: "Verzekerde(n)",
      accessor: "verzekerden",
    },
  ];

  if ((aoVerzekering.dekkingen?.length ?? 0) > 1) {
    aoVerzekering.dekkingen?.forEach((dekking, index) => {
      result.push({
        header:
          dekking.naamDekking === "Arbeidsongeschiktheidsdekking"
            ? "Premie dekking arbeidsongeschiktheid "
            : dekking.naamDekking === "Werkloosheidsdekking"
            ? "Premie dekking werkloosheid"
            : "Maandbetaling",
        accessor: (data: AoVerzekering) => {
          return dekking.premieBedrag
            ? `${bedragFormat(dekking.premieBedrag, 2)}, ${data.termjn}`
            : null;
        },
        dataType: "currency-with-decimals",
      });
    });
  }

  /* istanbul ignore next */
  result.push(
    {
      header:
        (aoVerzekering.dekkingen?.length ?? 0) > 1
          ? "Premie totaal, termijn"
          : "Premie, termijn",
      accessor: (data: AoVerzekering) => {
        return data.termijnPremie
          ? `${bedragFormat(data.termijnPremie, 2)}, ${data.termjn}`
          : null;
      },
    },
    {
      header: "Premie aftrekbaar AO",
      accessor: (data: AoVerzekering) =>
        data.premieAftrekbaarAo === null
          ? null
          : data.premieAftrekbaarAo
          ? "Ja"
          : "Nee",
    },
    {
      header: "Collectief",
      accessor: (data: AoVerzekering) =>
        data.collectief === null ? null : data.collectief ? "Ja" : "Nee",
    }
  );

  aoVerzekering.dekkingen?.forEach((dekking: Dekking) => {
    const isWoonlastenverzekering =
      aoVerzekering.soortProduct === "Woonlastenverzekering";
    if (dekking.naamDekking === "Arbeidsongeschiktheidsdekking") {
      result.push(
        {
          header: "Dekking arbeidsongeschiktheid",
          accessor: () => " ",
          style: "margin-top:20px;font-weight: bold;",
        },
        {
          header: "Keuze dekking",
          accessor: () => dekking.keuzeDekking,
        },
        {
          header: "Verzekerd bedrag per maand",
          accessor: () => dekking.verzekerdBedrag,
          dataType: "currency-with-decimals",
        },
        {
          header: "Verzekerd bedrag na eerste jaar AO",
          accessor: () => dekking.verzekerdBedragVervolgjaren,
          dataType: "currency-with-decimals",
        },
        {
          header: "Jaarlijkse indexatie",
          accessor: () => dekking.indexatiePercentage,
          dataType: "percentage",
        },
        {
          header: "Soort indexatie",
          accessor: () => dekking.soortIndexatie,
        },
        {
          header: "Eindleeftijd uitkering",
          accessor: () =>
            dekking.eindleeftijdUitkering ?? 0
              ? `${dekking.eindleeftijdUitkering} jaar`
              : null,
        },
        {
          header: "Percentage",
          accessor: () => dekking.basisDekkingPercentage,
          dataType: "percentage",
        },
        {
          header: "Maximale uitkeringsduur",
          accessor: () => dekking.maxUitkeringsDuur,
        },
        {
          header: "Waarvan eigen risico periode",
          accessor: () => dekking.eigenRisicoPeriode,
        },
        {
          header: "Beoordelingscriterium arbeidsongeschiktheid",
          accessor: () => dekking.beoordelingsCriterium,
        },
        {
          header: "Uitkering bij gedeeltelijke arbeidsongeschiktheid",
          accessor: () =>
            getUitkeringBijGedeeltelijkeArbeidsongechiktheid(
              dekking.uitkeringBijAo
            ),
        },
        {
          header: `Uitkeringsdrempel (AO%)${
            isWoonlastenverzekering ? "" : " (1e jaar)"
          }`,
          accessor: () => dekking.uitkeringsdrempel,
          dataType: "percentage",
        }
      );
      if (!isWoonlastenverzekering) {
        result.push(
          {
            header: "Beoordelingscriterium arbeidsongeschiktheid vervolgjaren",
            accessor: () => dekking.beoordelingsCriteriaumVervolgjaren,
          },
          {
            header: "Uitkeringsdrempel (AO%) vervolgjaren",
            accessor: () => dekking.uitkeringsdrempelVervolgJaren,
            dataType: "percentage",
          }
        );
      }
    }
    if (dekking.naamDekking === "Werkloosheidsdekking") {
      result.push(
        {
          header: "Dekking werkloosheid",
          accessor: () => " ",
          style: "font-weight: bold;",
        },
        {
          header: "Verzekerd bedrag per maand",
          accessor: () => dekking.verzekerdBedrag,
          dataType: "currency-with-decimals",
        },
        {
          header: "Uitkeringsduur",
          accessor: () => dekking.maxUitkeringsDuur,
        }
      );
    }
    if (dekking.naamDekking === "Ernstige aandoeningen dekking") {
      result.push(
        {
          header: "Dekking ernstige aandoeningen",
          accessor: () => " ",
          style: "font-weight: bold;",
        },
        {
          header: "Verzekerd bedrag per maand",
          accessor: () => dekking.verzekerdBedrag,
          dataType: "currency-with-decimals",
        },
        {
          header: "Vaste maximale uitkering",
          accessor: () => dekking.maxUitkeringBedrag,
          dataType: "currency-with-decimals",
        },
        {
          header: "Maximale uitkeringsduur",
          accessor: () => dekking.maxUitkeringsDuur,
        }
      );
    }
  });

  return result;
};
